// utils/depositRefundIdempotency.ts
//
// Explicit attempt-lifecycle state machine + canonical payload/fingerprint
// helpers for POST /deposits/{deposit_id}/refund, used by
// pages/deposits/DepositDetail.tsx -- the only frontend caller of this
// endpoint.
//
// Per-deposit slot, NOT a single global slot: mirrors
// utils/depositWriteoffIdempotency.ts's design exactly -- one independent
// attempt slot PER deposit_id, so that an unresolved ambiguous attempt on
// one Deposit can never block, leak into, or be confused with a submission
// on a different Deposit. This module itself only models the state machine
// for a SINGLE slot (idle/in-flight/ambiguous) -- it has no notion of
// "which deposit" at all. The caller (DepositDetail.tsx) is responsible for
// keeping one DepositRefundAttempt PER deposit_id (e.g. a
// Map<number, DepositRefundAttempt>) and always passing THIS deposit's own
// slot into decideDepositRefundAttempt / shouldRestoreAmbiguousAttempt --
// never a single shared instance. This is deliberately NOT the same shape
// as utils/fixedAssetPaymentIdempotency.ts's older global cross-resource
// block, for the identical reason depositWriteoffIdempotency.ts rejects it.
//
// Scoped to Refund ONLY: Write-off and Refund are different operations and
// must never share or block each other -- this module has no notion of
// Write-off at all, so a DepositRefundAttempt can never represent or be
// confused with a Write-off attempt. Refund and write-off keep entirely
// separate Maps in DepositDetail.tsx, even for the same deposit_id.

export type DepositRefundPayload = {
  deposit_id: number;
  amount: string;
  bank_account_id: number;
  date: string;
  memo: string | null;
};

/**
 * Deterministic canonical payload for POST /deposits/{deposit_id}/refund.
 * Callers must pass the SAME object (with idempotency_key added) as the
 * actual request body, never a separately reconstructed copy, so the
 * fingerprint can never drift from what is truly sent.
 *
 * - deposit_id: canonicalized to a number, included even though it is also
 *   the path parameter -- the idempotency identity is only (company_id,
 *   operation_type, idempotency_key), so a key reused against a DIFFERENT
 *   deposit must still be caught as a conflict by the backend. Also used
 *   locally by the caller (never sent as its own field beyond this
 *   payload) to select which per-deposit attempt slot a submission reads
 *   and writes.
 * - amount: fixed 2-decimal string. This app has no frontend-visible
 *   company money-scale setting anywhere (see canonicalDepositWriteoff's
 *   identical rationale) -- every existing money input in this app assumes
 *   2 decimals client-side, matching the backend's default
 *   CompanySettings.money_scale of 2. Mirrors canonicalDepositWriteoff's /
 *   canonicalDepositCreate's identical `.toFixed(2)` choice, so "10",
 *   "10.0", "10.00" all canonicalize identically.
 * - bank_account_id: canonicalized to a number. Selects which specific
 *   account the refund money actually moves through -- the single
 *   highest-risk field in this whole design (see
 *   backend/deposits_router.py's _deposit_refund_canonical_payload
 *   docstring): a same-key retry with a DIFFERENT bank account must never
 *   silently replay against the original account.
 * - date: the exact submitted YYYY-MM-DD -- never re-derived from "today"
 *   at retry time. The backend's DepositRefundCreate.date is a required
 *   field specifically so the fingerprint is never a function of
 *   date.today() (see _deposit_refund_canonical_payload's docstring: an
 *   implicit "today" fallback would make an ambiguous retry that crosses
 *   midnight recompute a DIFFERENT fingerprint and false-conflict). This
 *   module's caller (DepositDetail.tsx) must always resubmit the SAME
 *   resolved date string across a retry of the same attempt, never
 *   re-reading a fresh "today" value from the date input's default.
 * - memo: trimmed, empty string canonicalized to null -- matching the
 *   backend's own single-normalization-point
 *   `(cmd.memo or "").strip() or None`. The current refund UI has no memo
 *   input field at all, so callers always pass "" here today, which
 *   canonicalizes to null -- but the parameter exists so this utility
 *   remains correct if a memo field is ever added, and so direct API
 *   callers supplying memo are still fingerprinted correctly.
 */
export function canonicalDepositRefund(
  depositId: string | number,
  amount: string | number,
  bankAccountId: string | number,
  date: string,
  memo: string,
): DepositRefundPayload {
  return {
    deposit_id: Number(depositId),
    amount: Number(amount).toFixed(2),
    bank_account_id: Number(bankAccountId),
    date,
    memo: memo.trim() || null,
  };
}

export function fingerprintDepositRefund(payload: DepositRefundPayload): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Attempt lifecycle -- ONE slot's state machine. The caller owns a
// collection of these (one per deposit_id), never a single shared instance.
// ---------------------------------------------------------------------------

/**
 * - "idle": no active attempt for this deposit. The next submission always
 *   mints a fresh key.
 * - "in-flight": a request has been sent for this deposit and its outcome
 *   is not yet known. Transitions to "idle" (success or definite
 *   rejection) or "ambiguous" once the response (or its absence) is
 *   classified.
 * - "ambiguous": this deposit's last refund attempt outcome could not be
 *   determined (network failure, timeout, unreadable response, HTTP 500,
 *   or any other unexpected server error). The key/fingerprint/payload are
 *   retained so an unchanged retry safely replays instead of risking a
 *   duplicate refund; an edited retry stays blocked until this specific
 *   attempt is resolved. A DIFFERENT deposit's own slot -- and this same
 *   deposit's write-off attempt slot, tracked in a completely separate Map
 *   -- are entirely unaffected by this deposit's refund being ambiguous.
 */
export type DepositRefundAttemptState = "idle" | "in-flight" | "ambiguous";

export type DepositRefundAttempt = {
  state: DepositRefundAttemptState;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: DepositRefundPayload | null;
};

export const IDLE_DEPOSIT_REFUND_ATTEMPT: DepositRefundAttempt = {
  state: "idle",
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

export type DepositRefundSubmitDecision =
  | { kind: "start"; idempotencyKey: string; attempt: DepositRefundAttempt }
  | { kind: "blocked-mismatch" }
  | { kind: "blocked-in-flight" };

/**
 * Pure decision for whether a submit attempt may proceed to the network,
 * given the CURRENT attempt state for THIS deposit's own refund slot (the
 * caller is responsible for looking up the right slot -- e.g. from a
 * Map<number, DepositRefundAttempt> keyed by deposit_id -- before calling
 * this) and the canonical payload/fingerprint just built from the form.
 * Never mutates `attempt` -- returns a NEW attempt (state "in-flight") for
 * the caller to store back into that same slot when `kind === "start"`.
 *
 * This function has no concept of "a different deposit" at all, and no
 * concept of "write-off" at all -- there is nothing here analogous to
 * fixedAssetPaymentIdempotency.ts's blocked-cross-asset. Each deposit's
 * refund slot is independent by construction: a deposit with no retained
 * refund attempt is always idle, regardless of what any other deposit's
 * refund slot -- or this same deposit's write-off slot -- currently holds.
 *
 *  - idle -> always starts a fresh attempt with a newly generated key.
 *  - ambiguous, SAME fingerprint -> unchanged retry: reuses the stored key
 *    exactly (never mints a new one for an ambiguous outcome -- see module
 *    docstring).
 *  - ambiguous, DIFFERENT fingerprint -> blocked-mismatch: the prior
 *    attempt under this key may already have committed; an edited
 *    resubmission under the same key must never be sent.
 *  - in-flight -> blocked-in-flight: defensive. The component's own
 *    synchronous in-flight ref already prevents this from being reached
 *    in practice, but the decision stays correct even if called before
 *    that guard, or from a test, without it.
 */
export function decideDepositRefundAttempt(
  attempt: DepositRefundAttempt,
  payload: DepositRefundPayload,
  fingerprint: string,
  generateKey: () => string,
): DepositRefundSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  if (attempt.state === "ambiguous") {
    if (attempt.submittedFingerprint !== fingerprint) {
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

/** Confirmed 2xx success (create or successful replay -- both return the
 * same response shape, so no distinction is needed here). Clears this
 * deposit's refund slot back to idle -- the caller should remove/overwrite
 * ONLY this deposit's refund entry, never touch any other deposit's slot,
 * and never touch this deposit's own write-off slot (a separate Map). */
export function resolveDepositRefundSuccess(): DepositRefundAttempt {
  return IDLE_DEPOSIT_REFUND_ATTEMPT;
}

/**
 * Definite pre-commit rejection: any deterministic 4xx this endpoint's
 * stack can produce -- 400/404 raised by this handler itself, 401/403
 * raised by its auth dependencies before the handler body ever runs, 422
 * from FastAPI's own request-validation short-circuit (including a missing
 * `date`, now a required field -- see schemas.DepositRefundCreate), a
 * closed-period 409, or an idempotency-conflict 409. In every one of these
 * cases nothing was persisted under this key (either the handler never ran
 * at all, or create() raised before the claim-insert savepoint could
 * complete -- see backend/services/idempotency.run_idempotent_operation),
 * so the identity is free to reuse. Returns to idle so the next submission
 * mints a fresh key and is treated as a new logical attempt, with the user
 * free to edit any field. Only THIS deposit's refund slot is affected.
 */
export function resolveDepositRefundDefiniteRejection(): DepositRefundAttempt {
  return IDLE_DEPOSIT_REFUND_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/malformed/unreadable
 * response, HTTP 500, or any other unexpected server error where the
 * operation may have committed before the response was constructed (or
 * before replay validation ran). Retains the key, fingerprint, and
 * submitted payload so an unchanged retry safely replays instead of
 * risking a duplicate; an edited retry stays blocked. Only THIS deposit's
 * refund slot transitions -- every other deposit's own refund slot, and
 * this deposit's own write-off slot, remain untouched and independently
 * usable.
 */
export function resolveDepositRefundAmbiguousFailure(
  attempt: DepositRefundAttempt,
): DepositRefundAttempt {
  return { ...attempt, state: "ambiguous" };
}

/**
 * Whether the refund form should repopulate this deposit's previously
 * submitted form values (date/bank account/amount/memo) instead of
 * resetting to fresh defaults. True only when the slot passed in is a live
 * AMBIGUOUS attempt.
 *
 * Restoring matters: resetting to fresh defaults (e.g. today's date, the
 * deposit's default bank account) would almost never coincidentally match
 * what was actually submitted, so the very next submission would compute a
 * different fingerprint and get blocked as an "edited retry" -- trapping
 * the user with no visible way to make an unchanged retry that safely
 * reuses the key.
 *
 * The caller must pass THIS deposit's own refund slot (never a different
 * deposit's, and never this deposit's write-off slot) -- since this
 * function has no depositId parameter at all, correct scoping is entirely
 * the caller's responsibility (look the slot up from its Map by the
 * CURRENT deposit_id before calling this).
 */
export function shouldRestoreAmbiguousAttempt(attempt: DepositRefundAttempt): boolean {
  return attempt.state === "ambiguous";
}

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type DepositRefundFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error into one of the four outcomes this
 * endpoint can produce. Deliberately does NOT classify by HTTP status
 * alone for 409: both a closed-period rejection and an idempotency-
 * conflict rejection share HTTP 409 (see backend/utils/accounting_guards.py's
 * _assert_not_closed, and backend/deposits_router.py's
 * IdempotencyRequestConflict handling) but require different messaging and
 * a different interpretation -- a closed-period 409 is detected via
 * `isClosedPeriod`'s message match; any OTHER 409 is treated as an
 * idempotency conflict, mirroring classifyDepositWriteoffFailure's
 * identical reasoning.
 *
 * Every OTHER 4xx status this endpoint's stack can actually produce --
 * 400/404 raised directly by this handler, 401/403 raised by auth
 * dependencies (resolve before the handler body runs at all), and 422 from
 * FastAPI's own RequestValidationError handler (also short-circuits before
 * the handler runs, including a missing `date`) -- is treated as a
 * definite rejection uniformly: every one of these occurs strictly before
 * (or in place of) any business write, so nothing was ever persisted under
 * the key.
 *
 * `status` (added to apiPost's thrown error by api/client.ts) is what
 * distinguishes a genuine HTTP response (4xx/5xx) from a network-level
 * failure: fetch() itself rejecting (network down, DNS, CORS, or a
 * connection-level timeout) throws a plain Error/TypeError with no
 * `.response` at all, so `err?.response?.status` is `undefined` for
 * those -- correctly folded into "ambiguous" here, the same as a
 * malformed/unreadable response.
 */
export function classifyDepositRefundFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): DepositRefundFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    // Network failure, timeout, or any other rejection with no HTTP
    // response at all -- outcome unknown, must be treated as ambiguous.
    return "ambiguous";
  }

  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }

  if (status >= 400 && status < 500) {
    // Every other 4xx (400, 401, 403, 404, 422, and any other well-formed
    // 4xx) -- see the module-level rationale above. Deterministic: nothing
    // was ever persisted under this key.
    return "definite-rejection";
  }

  // 5xx (500 and any other), and anything outside the 4xx/409 ranges this
  // app's own code never emits: ambiguous -- the backend's replay loader
  // (_load_deposit_refund) runs AFTER db.commit(), so a 500 can mean the
  // refund WAS persisted and only response construction or replay
  // validation failed. Fails closed for anything not positively identified
  // as a definite pre-commit rejection.
  return "ambiguous";
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * Shown when an edited retry is blocked while an ambiguous attempt for
 * THIS deposit's refund remains unresolved. Deliberately does NOT say
 * reloading alone is safe -- a reload proves nothing about whether the
 * prior attempt already posted. Must convey all three ideas: (1) the
 * prior attempt may already have been recorded, (2) restoring the
 * original values permits a safe retry under the same key, (3) the user
 * should check this deposit's history before starting a new refund.
 */
export const DEPOSIT_REFUND_EDITED_RETRY_BLOCKED_MESSAGE =
  "This refund may already be recorded. Retry with the original details, or check the history below first.";

/**
 * Shown for an idempotency-conflict 409: the current key is permanently
 * bound to a different (already-completed) request and can never succeed
 * for this payload. Deliberately does NOT suggest the refund may
 * ambiguously have been recorded -- that is not the situation; THIS
 * attempt's data was never persisted under this key.
 */
export const DEPOSIT_REFUND_CONFLICT_MESSAGE =
  "Couldn't confirm this refund. Check the history below before trying again.";
